import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLang } from "@/contexts/LanguageContext";
import { api } from "@/lib/api";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { CalendarDays, ChevronRight, Loader2, Trash2 } from "lucide-react";

function isSameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

export default function CalendarPage() {
  const { t, isRTL } = useLang();
  const nav = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [date, setDate] = useState(new Date());

  const load = () => {
    setLoading(true);
    api.get("/scheduled-events").then((r) => setEvents(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const remove = async (id, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/scheduled-events/${id}`);
      setEvents((prev) => prev.filter((x) => x.id !== id));
    } catch {}
  };

  // Events grouped by date string
  const eventsByDay = useMemo(() => {
    const map = new Map();
    for (const e of events) {
      const d = new Date(e.scheduled_at);
      const key = d.toDateString();
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(e);
    }
    return map;
  }, [events]);

  const markedDays = useMemo(() => Array.from(eventsByDay.keys()).map((k) => new Date(k)), [eventsByDay]);
  const dayEvents = eventsByDay.get(date.toDateString()) || [];

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8" data-testid="calendar-page">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--secondary))] font-bold">{t("calendar")}</div>
        <h1 className="mt-1 text-3xl sm:text-4xl font-heading font-semibold tracking-tight">{t("yourCalendar")}</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Calendar */}
        <div className="bg-white border border-[hsl(var(--border))] rounded-3xl p-4 sm:p-6 shadow-[0_4px_24px_rgba(31,30,27,0.04)]" data-testid="calendar-widget">
          <Calendar
            mode="single"
            selected={date}
            onSelect={(d) => d && setDate(d)}
            modifiers={{ marked: markedDays }}
            modifiersClassNames={{
              marked: "relative font-semibold text-[hsl(var(--primary))]",
            }}
            className="w-full"
            classNames={{
              months: "w-full",
              month: "w-full space-y-4",
              caption_label: "font-heading text-lg",
              table: "w-full",
              head_row: "flex w-full",
              head_cell: "flex-1 text-xs text-[hsl(var(--muted-foreground))] font-medium",
              row: "flex w-full mt-1",
              cell: "flex-1 text-center p-0",
              day: "h-10 w-full rounded-xl hover:bg-[hsl(var(--elevated))] text-sm",
              day_selected: "bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary-hover))]",
              day_today: "ring-1 ring-[hsl(var(--primary))]",
              day_outside: "opacity-30",
            }}
          />
          <div className="mt-3 flex items-center gap-2 text-xs text-[hsl(var(--muted-foreground))]">
            <span className="inline-block h-2 w-2 rounded-full bg-[hsl(var(--primary))]" />
            {t("hasEvents")}
          </div>
        </div>

        {/* Day list */}
        <div className="bg-white border border-[hsl(var(--border))] rounded-3xl p-5 sm:p-6 shadow-[0_4px_24px_rgba(31,30,27,0.04)]">
          <div className="flex items-center gap-2 mb-4">
            <div className="h-9 w-9 rounded-xl bg-[hsl(var(--elevated))] flex items-center justify-center">
              <CalendarDays className="h-4 w-4 text-[hsl(var(--primary))]" />
            </div>
            <div>
              <div className="font-heading font-semibold">{date.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}</div>
              <div className="text-xs text-[hsl(var(--muted-foreground))]">{dayEvents.length} {dayEvents.length === 1 ? t("event") : t("eventsLabel")}</div>
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-[hsl(var(--primary))]" /></div>
          ) : dayEvents.length === 0 ? (
            <div className="text-center py-8 text-sm text-[hsl(var(--muted-foreground))]" data-testid="calendar-empty">
              {t("nothingScheduled")}
            </div>
          ) : (
            <ul className="space-y-2">
              {dayEvents.map((ev) => (
                <li key={ev.id} className="group">
                  <div className="w-full flex items-center gap-3 p-3 rounded-2xl border border-[hsl(var(--border))] hover:border-[hsl(var(--primary))] transition-all">
                    <div className="h-10 w-10 rounded-xl bg-[hsl(var(--elevated))] overflow-hidden shrink-0">
                      {ev.image_b64 && <img src={`data:image/png;base64,${ev.image_b64}`} alt="" className="w-full h-full object-cover" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{ev.title}</div>
                      <div className="text-[11px] text-[hsl(var(--muted-foreground))] flex items-center gap-2">
                        <span className="uppercase tracking-wider font-semibold">{ev.category}</span>
                        <span>·</span>
                        <span>{new Date(ev.scheduled_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                      </div>
                    </div>
                    <button
                      onClick={(e) => remove(ev.id, e)}
                      data-testid={`cal-delete-${ev.id}`}
                      aria-label="Remove"
                      className="opacity-0 group-hover:opacity-100 transition-opacity h-8 w-8 rounded-full hover:bg-[hsl(var(--destructive))]/10 flex items-center justify-center"
                    >
                      <Trash2 className="h-3.5 w-3.5 text-[hsl(var(--destructive))]" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
